import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const INACTIVITY_TIMEOUT = 90 * 1000; // 10 seconds
const YOUTUBE_VIDEO_ID = "YRmr6BHVThE";

const ScreenSaver: React.FC = () => {
  const [isActive, setIsActive] = useState(false);

  const resetTimer = useCallback(() => {
    setIsActive(false);
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const startTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setIsActive(true);
      }, INACTIVITY_TIMEOUT);
    };

    const handleActivity = () => {
      if (isActive) {
        // User interacted while screensaver is showing — dismiss it
        setIsActive(false);
      }
      startTimer();
    };

    // Listen for any user interaction
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "click",
      "scroll",
    ];
    events.forEach((e) =>
      window.addEventListener(e, handleActivity, { passive: true }),
    );

    // Start the initial timer
    startTimer();

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, handleActivity));
    };
  }, [isActive]);

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          key="screensaver"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center cursor-pointer"
          onClick={() => setIsActive(false)}
          onTouchStart={() => setIsActive(false)}
        >
          {/* YouTube embed — autoplay, muted, loop */}
          <div className="w-full h-full absolute inset-0">
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${YOUTUBE_VIDEO_ID}&controls=0&modestbranding=1&rel=0`}
              title="Screensaver"
              allow="autoplay; fullscreen"
              allowFullScreen
              style={{ border: "none", pointerEvents: "none" }}
            />
          </div>

          {/* Tap to continue overlay */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="absolute bottom-12 left-0 right-0 flex justify-center"
          >
            <div className="bg-black/60 backdrop-blur-sm text-white px-8 py-4 rounded-full text-lg font-semibold tracking-wide">
              Tap anywhere to continue
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScreenSaver;
